import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FilterService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { PanelModule } from 'primeng/panel';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { Subject } from 'rxjs';
import { CardpageBaseComponent } from '../../abstract/cardpage-base/cardpage-base.component';
import { UserStatusRowComponent } from '../../components/user-status-row/user-status-row.component';
import { DepartmentDialogComponent } from '../../dialogs/department-dialog/department-dialog.component';
import { OrganizationDialogComponent } from '../../dialogs/organization-dialog/organization-dialog.component';
import { ServiceDialogComponent } from '../../dialogs/service-dialog/service-dialog.component';
import { UserDialogComponent } from '../../dialogs/user-dialog/user-dialog.component';
import { Role } from '../../enums/role';
import { RoleMap } from '../../models/configuration';
import { DashboardAdmin, ServiceStatus, UserStatus } from '../../models/dashboard';
import { Department } from '../../models/department';
import { Organization } from '../../models/organization';
import { Service } from '../../models/service';
import { User } from '../../models/user';
import { ApiService } from '../../services/api.service';
import { UiNotificationService } from '../../services/ui-notification.service';
import { Functions } from '../../utilities/functions';
import { Parameters } from '../../utilities/parameters';
import { Reset } from '../../utilities/reset';

@Component({
  selector: 'app-dashboard-admin',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    FormsModule,
    CardpageBaseComponent,
    CardModule,
    PanelModule,
    ButtonModule,
    DepartmentDialogComponent,
    OrganizationDialogComponent,
    UserDialogComponent,
    ServiceDialogComponent,
    TableModule,
    TagModule,
    SelectModule,
    TranslatePipe,
    UserStatusRowComponent
  ],
  templateUrl: './dashboard-admin.component.html',
  styles: ``,
})
export class DashboardAdminComponent implements OnInit, OnDestroy {
  messageService = inject(UiNotificationService);
  private filterService = inject(FilterService);
  private translate = inject(TranslateService);

  loading: boolean = false;
  dtTrigger: Subject<boolean> = new Subject<boolean>();

  isSuperAdmin: boolean = false;

  dashboard: DashboardAdmin = Reset.dashboardAdmin();

  showDialogDept: boolean = false;
  showDialogOrg: boolean = false;
  showDialogService: boolean = false;
  showDialogUser: boolean = false;

  newDept: Department = Reset.department();
  newOrg: Organization = Reset.organization();
  selectedService: Service = Reset.service();
  newUser: User = Reset.user();

  isNewUser: boolean = false;

  isShowing: boolean = false;

  // 1. La tua lista sorgente (immutabile o gestibile anch'essa via signal)
  readonly roles: RoleMap[] = Functions.getRoles();

  // 2. Creiamo un signal reattivo che emette l'oggetto del dizionario ogni volta che la lingua cambia
  private keysToTranslate = this.roles.map(r => r.value);
  private currentTranslations = toSignal(this.translate.stream(this.keysToTranslate), { initialValue: {} as Record<string, string> });

  // 3. roleOptions diventa un Computed Signal derivato: si aggiorna da solo se cambiano le traduzioni
  readonly roleOptions = computed<any[]>(() => {
    const translations = this.currentTranslations();

    return this.roles.map(role => ({
      id: role.id,
      value: translations[role.value] || role.value
    }));
  });

  organizationOptions: { id: number; value: string }[] = [];
  selectedOrganization: { id: number; value: string } = { id: 0, value: '' };

  maxRole: number = Role.patient;

  constructor(private apiService: ApiService) {
  }

  ngOnInit(): void {
    this.maxRole = Functions.getMaxRoleUsable(Parameters.loggedUser.roles);
    this.filterService.register('arrayHasRole', (value: any[], filter: any): boolean => {
      // Se il filtro è vuoto, non filtrare nulla (mostra la riga)
      if (filter === undefined || filter === null || filter === '') {
        return true;
      }

      // Se la riga non ha dati o l'array dei ruoli è vuoto, nascondila
      if (!value || !Array.isArray(value)) {
        return false;
      }

      // Converte esplicitamente il valore a numero per evitare disallineamenti di tipo string/number
      const filterNumber = Number(filter);

      // Ritorna true se il numero del filtro è presente nell'array dei ruoli della riga
      return value.includes(filterNumber);
    });

    this.isSuperAdmin = Parameters.loggedUser.roles.includes(Role.superAdmin);

    this.setOrganizations();

    this.loadData();
  }

  ngOnDestroy() {
    this.dtTrigger.unsubscribe();
  }

  getRoleSeverity(role: number) {
    return Functions.getRoleSeverity(role);
  }

  getServiceName(id: number): string {
    return Functions.getServiceName(id);
  }

  getServiceStatusName(status: number): string {
    switch (status) {
      case -1:
        return 'deactivated';
      case 0:
        return 'expired';
      case 1:
        return 'expiring';
      case 2:
        return 'active';
      default:
        return 'invalid';
    }
  }

  getServiceStatusSeverity(status: number) {
    switch (status) {
      case -1:
        return 'info';
      case 0:
        return 'danger';
      case 1:
        return 'warn';
      case 2:
        return 'success';
      default:
        return 'danger';
    }
  }

  onDepartmentSaved(dept: Department) {
  }

  onNewDept(): void {
    this.newDept = Reset.department();
    this.showDialogDept = true;
  }

  onNewOrg(): void {
    this.newOrg = Reset.organization();
    this.showDialogOrg = true;
  }

  onNewUser(): void {
    this.apiService.getUsableOrganizations().subscribe({
      next: (o) => {
        this.isNewUser = true;
        this.newUser = Reset.user();
        const localMap = new Map(Parameters.loggedUser.organizations.map(obj => [obj.organizationId, obj]));
        this.newUser.organizations = o.map(org => {
          // Controlla se esiste una versione aggiornata in locale
          if (localMap.has(org.organizationId)) {
            return { ...org, ...localMap.get(org.organizationId) }; 
            // Nota: usando lo spread operator {...objServer, ...locale} unisci le proprietà,
            // mantenendo quelle del server che non sono presenti nel locale.
          }
          return org; // Altrimenti restituisce l'oggetto originale del server
        });
        this.showDialogUser = true;
      },
      error: (err: HttpErrorResponse) => {
        this.messageService.showError(err);
      }
    });
  }

  onOrganizationChange(event: any) {
    this.loadData();
  }

  onOrgSaved(org: Organization) {
  }

  onServiceSaved(service: Service) {
  }

  onShowService(service: ServiceStatus) {
    this.apiService.getService(service.serviceId).subscribe({
      next: (s) => {
        this.selectedService = s;
        this.showDialogService = true;
      },
      error: (err: HttpErrorResponse) => {
        this.messageService.showError(err);
      }
    });
  }

  onShowUser(user: UserStatus) {
    this.isShowing = true;
    this.isNewUser = false;

    this.apiService.getUser(user.id).subscribe({
      next: (u) => {
        this.newUser = u;

        this.apiService.getUserConsents(user.id).subscribe({
          next: (c) => {
            this.newUser.consents = c;
            this.showDialogUser = true;

            this.isShowing = false;
          },
          error: (err: HttpErrorResponse) => {
            this.isShowing = false;
            this.messageService.showError(err);
          }
        })
      },
      error: (err: HttpErrorResponse) => {
        this.isShowing = false;
        this.messageService.showError(err);
      }
    });
  }

  onUserSaved(user: User) {
    this.resetSelected();
    this.loadData();
  }

  private loadData(): void {
    this.loading = true;

    this.apiService.getDashboardAdmin(this.selectedOrganization.id).subscribe({
      next: (data) => {
        this.dashboard = data;
        this.dtTrigger.next(true);
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.messageService.showError(err);
      },
      complete: () => {
        this.loading = false;
      }
    })
  }

  private resetSelected(): void {
    this.newUser = Reset.user();
  }

  private setOrganizations() {
    if (this.isSuperAdmin) {
      this.apiService.getOrganizations().subscribe({
        next: (data) => {
          this.organizationOptions = data.map(org => ({
            id: org.organizationId,
            value: org.name
          }));
        },
        error: (err: HttpErrorResponse) => {
          this.messageService.showError(err);
        }
      })
    } else {
      this.organizationOptions = Parameters.loggedUser.organizations.map(org => ({
        id: org.organizationId,
        value: org.name
      }));
    }

    this.selectedOrganization = Parameters.loggedUser.organizations.length > 0 ?
      { id: Parameters.loggedUser.organizations[0].organizationId, value: Parameters.loggedUser.organizations[0].name } :
      { id: 0, value: '' };
  }
}
